'use client';

import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootState } from '@/redux/Store';
import { setFilters } from '@/redux/slices/discoverySlice';
import {
  searchDiscoveryProfiles,
  loadMoreProfiles,
} from '@/redux/thunks/discoveryThunks';
import { DiscoveryFilters } from '@/redux/slices/discoverySlice';
import { LoadingSpinner } from '@/components';
import HorizontalFilterBar from './components/HorizontalFilterBar';
import ProfileSearchCard from './components/ProfileSearchCard';
import EmptyState from './components/EmptyState';
import './Discovery.scss';

const DiscoveryPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    profiles,
    spotlightProfiles,
    filters,
    pagination,
    isLoading,
    isLoadingMore,
    isLoadingSpotlight,
    error,
    hasLocation,
    showEmptyState,
  } = useSelector((state: RootState) => state.discovery);

  const observerTarget = useRef<HTMLDivElement>(null);

  // Load initial profiles on mount
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll using IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore && pagination.hasMore) {
          // Check if we haven't reached the 1000 profile limit
          if (profiles.length < 1000) {
            dispatch(loadMoreProfiles());
          }
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [dispatch, isLoadingMore, pagination.hasMore, profiles.length]);

  const handleFilterChange = useCallback((newFilters: Partial<DiscoveryFilters>) => {
    dispatch(setFilters(newFilters));
  }, [dispatch]);

  const handleSearch = useCallback(() => {
    dispatch(searchDiscoveryProfiles({ page: 1, limit: 20, append: false }));
  }, [dispatch]);

  // Determine which profiles to show
  const displayProfiles = showEmptyState && profiles.length === 0 ? [] : profiles;
  return (
    <div className="discovery-page">
      <div className="discovery-container">
        {/* Header */}
        <header className="discovery-header">
          <h1>Discover Profiles</h1>
          <p>Find profiles near you by distance</p>
        </header>

        {/* Horizontal Filter Bar */}
        <HorizontalFilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
        />

        {/* Error Message */}
        {error && !hasLocation && (
          <div className="discovery-error">
            <p>{error}</p>
            <p className="error-hint">Please set your home or touring location in settings to use discovery.</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && profiles.length === 0 && (
          <LoadingSpinner size="medium" message="Searching for profiles..." />
        )}

        {/* Profile Grid */}
        {!isLoading && displayProfiles.length > 0 && (
          <div className="discovery-content">
            <div className="profiles-grid">
              {displayProfiles.map((profile) => (
                <ProfileSearchCard key={profile.id} profile={profile} />
              ))}
            </div>

            {/* Profile Count & Limit Warning */}
            <div className="profiles-count">
              Showing {profiles.length} profile{profiles.length !== 1 ? 's' : ''}
              {profiles.length >= 1000 && (
                <span className="count-warning"> (maximum reached)</span>
              )}
            </div>

            {/* Infinite Scroll Trigger */}
            {pagination.hasMore && profiles.length < 1000 && (
              <div ref={observerTarget} className="scroll-trigger">
                {isLoadingMore && (
                  <LoadingSpinner size="small" message="Loading more..." />
                )}
              </div>
            )}
          </div>
        )}

        {/* Empty State with Spotlight Profiles */}
        {showEmptyState && (
          <EmptyState
            hasLocation={hasLocation}
            isLoadingSpotlight={isLoadingSpotlight}
            spotlightProfiles={spotlightProfiles}
          />
        )}
      </div>
    </div>
  );
};

export default DiscoveryPage;
